/**
 * Composant React ForwardMessage
 *
 * Permet de transférer un message vers une nouvelle conversation, avec possibilité de résolution automatique.
 * Affiche un formulaire pour sélectionner le canal, renseigner l'expéditeur, le titre, la date, les destinataires en copie, et le contenu du mail.
 * Gère l'état local du formulaire, la sélection du canal, la validation de l'email, et l'envoi via Redux.
 *
 *
 * Fonctions principales :
 * - deleteResponse : supprime la réponse générée
 * - sendResponse : transfère le message et résout la conversation si demandé
 * - onChange : met à jour le payload du formulaire
 *
 * @returns {JSX.Element} Le composant de modale de transfert de message
 */
import lod_ from "lodash";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
	Grid,
	TextField,
	Button,
	Typography,
	IconButton,
	CircularProgress,
	Chip,
	Tooltip,
	FormGroup,
	FormControlLabel,
	Checkbox
} from "@material-ui/core";

import i18n from "i18n";
import { Close } from "@material-ui/icons";
import cockpitActions from "../../../../redux/actions/cockpitActions";
import SunEditor from "suneditor-react";
import { fr as French } from "suneditor/src/lang";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { fr } from "date-fns/locale";
import DateFnsUtils from "@date-io/date-fns";
import { Autocomplete } from "@material-ui/lab";
import GetChannelImage from "helpers/GetChannelImage";
import { regexMail } from "helpers/utilities";
import { ModalResolved } from "pages/Cockpit/ConvActions/ConvItemComponents";

/**
 * HSH Specific action to create an affair
 * @param {function} props.handleClose - Close the modal
 * @param {object} props.actionObject - Action object
 * @param {object|null} props.message - Message object
 * @returns {JSX.Element} - JSX component
 */
const ForwardMessage = ({ open, data = {}, handleClose, actionObject, message }) => {
	const dispatch = useDispatch();

	const [payload, setPayload] = useState({});
	const [spinner, setSpinner] = useState(true);
	const [currentAction, setCurrentAction] = useState(data);
	const [selectedChannel, setSelectedChannel] = useState({});
	const { assistantconfig } = useSelector(state => state);

	const [resolveAndForward, setResolveAndForward] = useState(false);

	const [showTooltip, setShowTooltip] = useState(false);

	const listFields = actionObject?.config?.listFields;
	const { selectedConversation } = useSelector(state => {
		return {
			selectedConversation: state.cockpit.selectedConversation
		};
	});

	const selectValue = lod_.get(selectedConversation, listFields) || {};

	useEffect(() => {
		let checkAction = false;
		if (selectedConversation.actions) {
			let correctAction = selectedConversation.actions.find(
				a => a.code === "analyseUnderstand" && a.state === "done"
			);
			if (correctAction) {
				setSpinner(false);
				setCurrentAction(correctAction);
				setPayload(correctAction?.result?.analyseResult || {});
				checkAction = true;
			}
		}

		if ((lod_.isNil(data) || lod_.isEmpty(data)) && open === true && checkAction === false) {
			dispatch(
				cockpitActions.launchActionFrontV2("analyseUnderstand", {
					header: {
						assistantID: selectedConversation.header.assistantID,
						fID: selectedConversation.header.fID,
						mID: message?.header?.mID,
						actionName: actionObject?.code
					},
					payload: {
						codeAction: actionObject?.code,
						parameters: {
							message: message?.payload?.payload?.text?.html_text
						}
					}
				})
			);
		} else {
			setSpinner(false);
		}
		if (
			!lod_.isNil(selectedConversation?.header?.extra?.channelCode) &&
			!lod_.isNil(assistantconfig?.channels)
		) {
			let channel = assistantconfig?.channels.find(
				c => c.code === selectedConversation?.header?.extra?.channelCode
			);
			if (!lod_.isNil(channel)) {
				setSelectedChannel(channel);
			}
		}
	}, []);

	useEffect(() => {
		if (currentAction.uuid && !lod_.isNil(selectedConversation.actions)) {
			let correctAction = selectedConversation.actions.find(a => a.uuid === currentAction.uuid);
			if (correctAction) {
				setCurrentAction(correctAction);
				setPayload(correctAction?.result?.analyseResult || {});
			}
		} else if (selectedConversation.actions) {
			let correctAction = selectedConversation.actions.find(
				a => a.code === "analyseUnderstand" && a.state === "done"
			);
			if (correctAction) {
				setSpinner(false);
				setCurrentAction(correctAction);
				setPayload(correctAction?.result?.analyseResult || {});
			}
		}
	}, [selectedConversation.actions]);

	const onChange = (path, value) => {
		setPayload(prev => {
			let clonedPayload = lod_.cloneDeep(prev);
			lod_.set(clonedPayload, path, value);
			return clonedPayload;
		});
	};

	// delete response received from UND
	const deleteResponse = () => {
		// Remove response from conversation & frontend
		try {
			dispatch(
				cockpitActions.launchActionFrontV2("deleteGeneratedAnswers", {
					header: {
						assistantID: selectedConversation.header.assistantID,
						fID: selectedConversation.header.fID
					},
					payload: {
						actionUuid: currentAction.uuid
					}
				})
			);
			setCurrentAction({});
			handleClose();
		} catch {
			// Gestion basique, à adapter selon UX
			setCurrentAction({});
			handleClose();
		}
	};

	const sendResponse = () => {
		// Remove response from conversation & frontend
		try {
			if (resolveAndForward === true) {
				const originalConversation = lod_.cloneDeep(selectedConversation);

				dispatch(
					cockpitActions.updateConversationState(
						"resolved",
						"byPassResolved",
						originalConversation,
						null
					)
				);
			}

			dispatch(
				cockpitActions.launchActionFrontV2("forwardMessageToNewConv", {
					header: {
						assistantID: selectedConversation.header.assistantID,
						fID: selectedConversation.header.fID,
						mID: message?.header?.mID
					},
					payload: {
						channel: selectedChannel,
						message: payload,
						actionUuid: currentAction?.uuid,
						attachements: message?.payload?.payload?.attachments || []
					}
				})
			);

			setCurrentAction({});
			handleClose();
		} catch {
			// Gestion basique, à adapter selon UX
			setCurrentAction({});
			handleClose();
		}
	};

	const [email, setEmail] = React.useState(payload?.sender || "");
	const [error, setError] = React.useState(false);

	const validateEmail = email => {
		const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return re.test(email);
	};

	return (
		<Dialog open={open} aria-labelledby="form-dialog-title" fullWidth maxWidth="lg">
			<DialogTitle id="draggable-dialog-title">
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center"
					}}
				>
					<div>{i18n.t("TabAction.forwardMessage")}</div>
					<div>
						<IconButton onClick={handleClose}>
							<Close />
						</IconButton>
					</div>
				</div>
			</DialogTitle>
			<DialogContent className="handle">
				{spinner ? (
					<Box
						display="flex"
						alignItems="center"
						justifyContent="center"
						width="100%"
						height="100%"
					>
						<CircularProgress color="inherit" size={100} />
					</Box>
				) : (
					<>
						<Typography variant="h5" gutterBottom>
							{i18n.t("TabAction.selectChannelForwardConvTitle")}
						</Typography>
						<Box sx={{ flexGrow: 1, mb: 2 }}>
							<span
								style={{
									display: "flex",
									flexWrap: "wrap",
									width: "100%"
								}}
							>
								{assistantconfig?.channels
									.filter(a => a.engage === true && a.type === "ML")
									.sort((a, b) => {
										// Priorité aux objets ayant `bool: true`
										if (a.favorite === true && b.favorite !== true) return -1;
										if (b.favorite === true && a.favorite !== true) return 1;

										// Ensuite, tri alphabétique sur `data`
										return a.name.localeCompare(b.name);
									})
									.map(channel => {
										return (
											<Box
												key={channel.code}
												style={{
													paddingRight: "2%",
													paddingTop: "2%"
												}}
											>
												<Chip
													label={channel.name}
													color={channel.code === selectedChannel.code ? "primary" : "default"}
													icon={
														<img
															src={GetChannelImage(
																assistantconfig?.channels,
																channel.code.toUpperCase(),
																channel
															)}
															alt="logo_dropdown"
															width="25"
															height="25"
															className="logo-channel"
														/>
													}
													clickable={true}
													onClick={() => {
														setSelectedChannel(channel);
													}}
												/>
											</Box>
										);
									})}
							</span>
						</Box>
						<Box sx={{ mb: 2 }}>
							<FormGroup row>
								<FormControlLabel
									control={
										<Checkbox
											color="primary"
											checked={resolveAndForward}
											onChange={e => setResolveAndForward(e.target.checked)}
											name="resolveAndForward"
										/>
									}
									label={i18n.t("TabAction.resolveAndForwardInfo")}
								/>
							</FormGroup>
						</Box>
						<Grid container justifyContent="space-between">
							<Grid container spacing={1}>
								{/* SENDER */}
								<Grid item xs={4}>
									<TextField
										variant="outlined"
										type="email"
										fullWidth
										value={payload?.sender || ""}
										label={i18n.t("COC.ACTIONS.forwardMessageSender")}
										onChange={event => {
											const val = event.target.value;
											setEmail(val);
											setError(val !== "" && !validateEmail(val));
											onChange("sender", val); // si tu veux aussi notifier le parent
										}}
										error={error}
										helperText={error ? i18n.t("COC.actionForm.FORMS.invalidEmail") : ""}
										InputLabelProps={{
											shrink: true
										}}
									/>
								</Grid>
								{/* TITLE */}
								<Grid item xs={4}>
									<TextField
										variant="outlined"
										type="text"
										fullWidth
										value={payload?.mailTitle || ""}
										label={i18n.t("COC.ACTIONS.forwardMessageTitle")}
										onChange={event => onChange("mailTitle", event.target.value)}
										InputLabelProps={{
											shrink: true
										}}
									/>
								</Grid>
								{/* DATE */}
								<Grid item xs={4}>
									<MuiPickersUtilsProvider locale={fr} utils={DateFnsUtils}>
										<KeyboardDatePicker
											fullWidth
											inputVariant="outlined"
											format="dd/MM/yyyy HH:mm"
											label={i18n.t("COC.ACTIONS.forwardMessageDate")}
											value={payload?.dateMessage || null}
											onChange={date => onChange("dateMessage", date)}
											disableToolbar
											onError={() => null}
											helperText=""
										/>
									</MuiPickersUtilsProvider>
								</Grid>
								<Grid item xs={4}>
									<Tooltip
										open={showTooltip}
										title={i18n.t("COC.actionForm.FORMS.invalidEmail")}
										placement="right"
									>
										<Autocomplete
											options={[]}
											multiple
											freeSolo
											value={lod_.isArray(payload?.cc) ? payload?.cc : []}
											onChange={(event, newValue) => {
												const filtered = newValue
													.map(item => (typeof item === "string" ? item.trim() : ""))
													.filter(email => !regexMail(email));

												if (filtered.length === newValue.length) {
													onChange("cc", filtered);
												} else {
													setShowTooltip(true);

													setTimeout(() => {
														setShowTooltip(false);
													}, 3000);
												}
											}}
											renderInput={params => (
												<TextField
													{...params}
													label={i18n.t("COC.ACTIONS.forwardMessageCC")}
													variant="outlined"
												/>
											)}
										/>
									</Tooltip>
								</Grid>
							</Grid>
						</Grid>

						{/* MAIL CONTENT forwardMessageContent*/}
						<Box pt={2}>
							<SunEditor
								showToolbar={false}
								onChange={content => onChange("mailContent", content)}
								setDefaultStyle="height: 250px; font-size: 16px; font-family: Roboto; font-weight: normal"
								setContents={payload?.mailContent || ""}
								onImageUploadBefore={() => {}}
								setOptions={{
									buttonList: [["fullScreen"]],
									pasteImage: false,
									lang: French,
									showPathLabel: false
								}}
							/>
						</Box>
					</>
				)}
			</DialogContent>
			<DialogActions
				style={{
					display: "flex",
					justifyContent: "space-between"
				}}
			>
				<Box>
					{currentAction.state === "done" && (
						<Button style={{ color: "red" }} onClick={deleteResponse}>
							{i18n.t("COC.Delete")}
						</Button>
					)}
				</Box>
				<Box>
					<Button style={{ color: "red" }} onClick={handleClose}>
						{i18n.t("TabAction.cancel")}
					</Button>
					<Button
						color="primary"
						autoFocus
						onClick={sendResponse}
						disabled={
							lod_.isEmpty(selectedChannel) ||
							lod_.isNil(payload?.sender) ||
							lod_.isEmpty(payload?.sender) ||
							error
						}
					>
						{i18n.t("COC.validate")}
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default ForwardMessage;
